import { useMemo } from "react";
import { Select, Group, Text, Stack } from "@mantine/core";
import { IconPhone } from "@tabler/icons-react";
import type {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
} from "@mantine/core";
import type { CallCompositionOption } from "@benchmark/core/types";
import { formatCallDate, formatCallDuration } from "@/utils/fhir/communication";

/**
 * Props for the call composition select component.
 * @agentic connections
 */
export type CallCompositionSelectProps = {
  options: CallCompositionOption[];
  value: string | null;
  onChange: (compositionId: string) => void;
  isPolling?: boolean;
  disabled?: boolean;
};

/**
 * Builds the label string shown in the closed dropdown for an option.
 * @agentic connections
 */
const buildOptionLabel = (option: CallCompositionOption): string => {
  const parts = [formatCallDate(option.date)];
  if (option.durationMs !== undefined) {
    parts.push(formatCallDuration(option.durationMs));
  }
  if (option.authorDisplay) {
    parts.push(option.authorDisplay);
  }
  return parts.join(" \u2022 ");
};

/**
 * Renders a custom dropdown option with date/duration and author.
 * @agentic connections
 */
const renderSelectOption = ({
  option,
}: ComboboxLikeRenderOptionInput<ComboboxItem>) => {
  const meta = (option as ComboboxItem & { meta?: CallCompositionOption }).meta;
  if (!meta) return <Text size="sm">{option.label}</Text>;

  const dateLabel = formatCallDate(meta.date);
  const durationLabel =
    meta.durationMs !== undefined ? formatCallDuration(meta.durationMs) : null;

  return (
    <Group gap="sm" wrap="nowrap">
      <IconPhone
        size={16}
        style={{ color: "var(--brand-fill)", flexShrink: 0 }}
      />
      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {dateLabel}
          </Text>
          {durationLabel && (
            <Text size="xs" c="dimmed">
              {durationLabel}
            </Text>
          )}
        </Group>
        {meta.authorDisplay && (
          <Text size="xs" c="dimmed">
            {meta.authorDisplay}
          </Text>
        )}
      </Stack>
    </Group>
  );
};

/**
 * Dropdown select for choosing which call composition to use for documentation prefill.
 * @agentic connections
 */
export const CallCompositionSelect = ({
  options,
  value,
  onChange,
  isPolling: _isPolling = false,
  disabled = false,
}: CallCompositionSelectProps) => {
  const data = useMemo(
    () =>
      options.map((opt) => ({
        value: opt.compositionId,
        label: buildOptionLabel(opt),
        meta: opt,
      })),
    [options],
  );

  return (
    <Select
      label="Select call"
      placeholder="Choose a call to document"
      data={data}
      value={value}
      onChange={(val) => {
        if (val) onChange(val);
      }}
      renderOption={renderSelectOption}
      disabled={disabled}
      allowDeselect={false}
      leftSection={<IconPhone size={16} />}
      size="sm"
    />
  );
};
